import { Link } from "react-router-dom";
import { ArrowRight, Eye, Target, Cpu, Radio, Plane, Car, Ship, FileCheck, Shield } from "lucide-react";
import { motion } from "framer-motion";

const capabilities = [
  {
    title: "Visual Target Pursuit",
    description: "Identifies, tracks and pursues targets using onboard camera and AI processing. Maintains continuous lock within field of view.",
    icon: Target,
  },
  {
    title: "Designed for GPS-Denied Operation",
    description: "Architecture designed to operate without GPS for the relevant guidance function. Subject to platform and sensor configuration.",
    icon: Radio,
  },
  {
    title: "Adaptive Guidance",
    description: "Dynamically adjusts heading and speed to compensate for target movement. Responds to changing conditions in real-time.",
    icon: Cpu,
  },
  {
    title: "Platform Integration",
    description: "Integrates with compatible platforms through defined interfaces. Specific integrations validated for supported configurations.",
    icon: Plane,
  },
];

const platforms = [
  { name: "Aerial", description: "Fixed-wing and multirotor UAVs with compatible interfaces", icon: Plane },
  { name: "Ground", description: "Unmanned ground vehicles with appropriate sensor mounts", icon: Car },
  { name: "Marine", description: "Surface and underwater vehicles with vision capability", icon: Ship },
];

const differentiators = [
  {
    title: "Designed for GPS-Denied",
    description: "Vision-based guidance is the primary navigation method—not a fallback when GPS fails. The architecture is designed from the ground up to operate without satellite navigation for the relevant guidance function.",
  },
  {
    title: "Deterministic Control",
    description: "Predictable, testable, explainable control logic. Critical for safety certification and mission planning. Behaviour can be validated before deployment.",
  },
  {
    title: "Lightweight & Efficient",
    description: "Designed to run on modest embedded compute. No GPU required for core guidance function. Suitable for SWaP-constrained platforms.",
  },
  {
    title: "Sensor Flexibility",
    description: "Compatible with RGB, thermal, and low-light imaging sensors. Effectiveness varies with sensor quality and environmental conditions.",
  },
];

export default function AIAVPlatformPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/platforms" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Platforms & Technology
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Eye className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              AI-AV
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Vision-Based Target-Oriented Adaptive Control
            </p>
            <p className="text-body max-w-2xl">
              AI-AV provides vision-based guidance for autonomous platforms. The system uses camera input to identify, track and pursue targets—designed to operate without GPS for the relevant guidance function.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What AI-AV Does */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              What AI-AV Does
            </h2>
            <p className="text-body mb-4">
              AI-AV is a guidance system that uses visual input to control autonomous platform movement toward a target. The system processes camera data in real-time, measures target position relative to the platform, and generates guidance commands.
            </p>
            <p className="text-body">
              The architecture is designed to operate without reliance on GPS or external positioning systems for the guidance function. This makes it suitable for environments where GPS is unavailable, unreliable, or contested.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">Core Functions</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <cap.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{cap.title}</h3>
                <p className="text-sm text-muted-foreground">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Support */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Platform Support</p>
            <h2 className="text-headline text-foreground">Integration Domains</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                className="card-pillar p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center mx-auto mb-4">
                  <platform.icon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{platform.name}</h3>
                <p className="text-sm text-muted-foreground">{platform.description}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8 max-w-2xl mx-auto italic">
            Platform-agnostic is an architectural objective. Specific integrations are validated for supported configurations and interfaces.
          </p>
        </div>
      </section>

      {/* Differentiators */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Approach</p>
            <h2 className="text-headline text-foreground">Key Differentiators</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {differentiators.map((diff, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-label text-foreground mb-2">{diff.title}</h3>
                <p className="text-sm text-muted-foreground">{diff.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IP Status */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center">
                <FileCheck className="h-5 w-5 text-muted-foreground" />
              </div>
              <h2 className="text-headline text-foreground">Intellectual Property</h2>
            </div>
            <div className="card-pillar p-8">
              <p className="text-body mb-4">
                AI-AV technology is protected by filed patent applications. The RLK Group maintains original, protected technology developed in-house.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Filed patent applications covering core guidance methodology</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">RLK-developed IP—not a third-party integration or dependency</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground italic">
                Note: "Filed patent application" status. Patent applications protect claimed subject matter, not every marketing statement made about a product.
              </p>
              <div className="mt-6">
                <Link to="/company/intellectual-property" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  View full IP portfolio →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration & Testing */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-6 text-center">
              Integration & Testing
            </h2>
            <div className="card-pillar p-8">
              <p className="text-body mb-6">
                AI-AV integration requires:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Compatible platform with defined control interfaces</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Suitable visual sensor with adequate resolution and frame rate</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Embedded compute platform meeting minimum requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Defined test environment for validation</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground italic">
                Performance varies with platform characteristics, sensor quality, and environmental conditions. Specific performance claims require validation in the target configuration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss an Integration
            </h2>
            <p className="text-body mb-8">
              Tell us about your platform, interface requirements, and the guidance capability you need. We will scope an integration approach and define test boundaries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=ai-av" className="btn-primary">
                Discuss an Integration
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link to="/contact?type=technical" className="btn-secondary">
                Request Technical Briefing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
